import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * @param {number} principalCents
 * @param {number} n
 * @param {boolean} withInterest
 * @param {number} monthlyRatePercent
 */
function installmentCents(principalCents, n, withInterest, monthlyRatePercent) {
  if (!n || n < 1) return 0;
  if (!withInterest || !monthlyRatePercent || monthlyRatePercent <= 0) {
    return Math.round(principalCents / n);
  }
  const r = monthlyRatePercent / 100;
  const pow = (1 + r) ** n;
  const denom = pow - 1;
  if (denom <= 0) return Math.round(principalCents / n);
  return Math.round((principalCents * r * pow) / denom);
}

/**
 * @param {number} cents
 * @param {string} locale
 */
function formatMoneyIntl(cents, locale) {
  try {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return String(Math.round(cents / 100));
  }
}

class ProductInstallments extends HTMLElement {
  /** @type {(event: Event) => void} */
  #onVariantUpdate = (event) => {
    const e = /** @type {VariantUpdateEvent} */ (event);

    if (e.detail.data.newProduct) {
      this.dataset.productId = e.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const resource = e.detail.resource;
    const priceCents = resource && 'price' in resource ? /** @type {{ price: number }} */ (resource).price : undefined;
    if (typeof priceCents !== 'number') return;

    this.#updateAmount(priceCents);
  };

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @param {number} priceCents
   */
  #updateAmount(priceCents) {
    const n = Number.parseInt(this.dataset.installmentCount ?? '3', 10) || 3;
    const withInterest = this.dataset.withInterest === 'true';
    const rate = Number.parseFloat(this.dataset.monthlyInterestPercent ?? '0') || 0;
    const locale = this.dataset.locale || 'es';

    const cents = installmentCents(priceCents, n, withInterest, rate);
    const formatted = formatMoneyIntl(cents, locale);

    const amountEl = this.querySelector('[data-installment-amount]');
    if (amountEl) {
      amountEl.textContent = formatted;
    }
  }
}

if (!customElements.get('product-installments')) {
  customElements.define('product-installments', ProductInstallments);
}
